import React, { useState, useEffect } from "react";
import {
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Label,
  Button,
} from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";
import LedgersService from "../../../services/AccountingServices/LedgerService";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Payment type name is required"),
  description: Yup.string().required("Description is required"),
  accountNumber: Yup.number()
    .min(0, "Account number must be greater than or equal to 0")
    .required("Account number is required"),
  code: Yup.string().required("Code is required"),
  ledgerId: Yup.number().required("Ledger is required"),
});

const EditModal = ({
  modal,
  toggleModal,
  editingPackage,
  savePackage,
  fetchPaymentType,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [ledgers, setLedgers] = useState([]);

  useEffect(() => {
    if (modal) {
      fetchLedgers();
    }
  }, [modal]);

  const fetchLedgers = async () => {
    try {
      const response = await LedgersService.getList();
      const data = Array.isArray(response) ? response : response?.items || [];
      const formattedLedgers = data.map((ledger) => ({
        value: ledger.id,
        label: `${ledger.name} (${ledger.code})`,
      }));
      setLedgers(formattedLedgers);
    } catch (error) {
      console.error("Error fetching ledgers:", error);
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.15)" : "none",
      "&:hover": { borderColor: "#86b7fe" },
      fontSize: "0.875rem",
    }),
    option: (base) => ({ ...base, fontSize: "0.875rem" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setSubmitting,
    resetForm,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: editingPackage?.id || null,
      name: editingPackage?.name || "",
      description: editingPackage?.description || "",
      accountNumber: editingPackage?.accountNumber || "",
      code: editingPackage?.code || "",
      ledgerId: editingPackage?.ledgerId || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("Name", values.name);
        formData.append("Description", values.description || "");
        formData.append("AccountNumber", values.accountNumber || "");
        formData.append("Code", values.code || "");
        formData.append("IsActive", true);
        formData.append("LedgerId", values.ledgerId || "");
        if (selectedImage) {
          formData.append("Image", selectedImage);
        }

        if (values?.id) {
          await PaymentTypeServices.update(values.id, formData);
          savePackage({ ...values });
          toast.success("Payment type updated successfully");
        } else {
          await PaymentTypeServices.create(formData);
          toast.success("Payment type created successfully");
          resetForm();
          setSelectedImage(null);
        }

        fetchPaymentType();
        toggleModal();
      } catch (error) {
        console.error("Error during package update:", error);
        toast.error(
          "Error updating package: " + (error.message || "Unknown error")
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedImage(file);
  };

  return (
    <Modal isOpen={modal} toggle={toggleModal} backdrop="static" centered>
      <ModalHeader toggle={toggleModal}>
        {editingPackage?.id ? "Edit" : "Add"} Payment Method
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <Row className="g-3">
            {/* Payment Name */}
            <Col lg={12}>
              <div className="mb-2">
                <Label className="fw-medium small">Payment Name <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="name"
                  placeholder="Enter name (e.g. Cash, Bank)"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.name && errors.name ? "is-invalid" : ""}
                />
                {touched.name && errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </Col>

            {/* Ledger Dropdown */}
            <Col lg={12}>
              <div className="mb-2">
                <Label className="fw-medium small">Accounting Ledger <span className="text-danger">*</span></Label>
                <Select
                  options={ledgers}
                  placeholder="Select a ledger"
                  value={ledgers.find((opt) => opt.value === values.ledgerId)}
                  onChange={(opt) => setFieldValue("ledgerId", opt ? opt.value : "")}
                  onBlur={() => handleBlur({ target: { name: "ledgerId" } })}
                  styles={selectStyles}
                  isSearchable
                  menuPortalTarget={document.body}
                />
                {touched.ledgerId && errors.ledgerId && (
                  <div className="text-danger small mt-1">{errors.ledgerId}</div>
                )}
              </div>
            </Col>

            {/* Account Number & Code */}
            <Col md={6}>
              <div className="mb-2">
                <Label className="fw-medium small">Account Number <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="accountNumber"
                  placeholder="Enter account number"
                  value={values.accountNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.accountNumber && errors.accountNumber ? "is-invalid" : ""}
                />
                {touched.accountNumber && errors.accountNumber && (
                  <div className="invalid-feedback">{errors.accountNumber}</div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-2">
                <Label className="fw-medium small">Short Code <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="code"
                  placeholder="e.g. CASH, BANK"
                  value={values.code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.code && errors.code ? "is-invalid" : ""}
                />
                {touched.code && errors.code && (
                  <div className="invalid-feedback">{errors.code}</div>
                )}
              </div>
            </Col>

            {/* Description */}
            <Col lg={12}>
              <div className="mb-2">
                <Label className="fw-medium small">Description</Label>
                <textarea
                  name="description"
                  rows="2"
                  placeholder="Enter a brief description..."
                  className={`form-control ${touched.description && errors.description ? "is-invalid" : ""}`}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
            </Col>

            {/* Image Upload */}
            <Col lg={12}>
              <div className="mb-0">
                <Label className="fw-medium small">Logo / Icon</Label>
                <div className="d-flex align-items-center gap-3">
                  <Input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="flex-grow-1"
                  />
                  {selectedImage && (
                    <div style={{ width: "40px", height: "40px", borderRadius: "4px", overflow: "hidden", border: "1px solid #ddd" }}>
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="preview"
                        className="w-100 h-100 object-fit-cover"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggleModal} className="fw-medium">
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={isSubmitting}
            className="fw-medium px-4"
          >
            {isSubmitting ? "Saving..." : (editingPackage?.id ? "Update" : "Add Method")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditModal;


